<script setup>
import { ref, computed } from 'vue'
import DxDropDownBox from 'devextreme-vue/drop-down-box'
import { DxTabPanel } from 'devextreme-vue/tab-panel'
import { DxDataGrid, DxSelection, DxScrolling, DxFilterRow } from 'devextreme-vue/data-grid'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'

const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
  },
  value: {
    type: Object,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isValid: {
    type: Boolean,
    default: true,
  },
  validationError: {
    type: Object,
    default: null,
  },
  entityDropDownOpened: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['valueChanged'])

const portalStore = usePortalStore()
const registryStore = useRegistryStore()

const activeTabIndex = ref(0)
const localizedDocumentTypes = portalStore.getLocalizedDocumentTypes()

let dropDown = null
const dataGrids = []
let keyboardSelectionDepth = 0
const dropDownOpened = ref(props.entityDropDownOpened)
const hideDropDown = ref(props.entityDropDownOpened)
const dropDownDataSource = props.value ? [{ id: props.value.id, _display: props.value._display }] : []

const onContentReady = (e) => {
  const rows = e.component.getVisibleRows()
  if (rows.length === 1) { e.component.selectRowsByIndexes([0]) }
  if (e.component === dataGrids[activeTabIndex.value]) focusActiveGrid()
}

const dropDownOptions = computed(() => ({
  ...(hideDropDown.value ? { width: 0, height: 0 } : { width: '50rem', height: '30rem' }),
  onShown: focusActiveGrid,
}))

const onClosed = () => {
  hideDropDown.value = false
}

const closeAndFocus = () => {
  dropDown.close()
  dropDown.focus()
}

const onDropDownKeyDown = (e) => {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    e.stopPropagation()
    if (dropDown.option('opened')) focusActiveGrid()
    else dropDown.open()
  } else if (['Escape', 'Tab'].includes(e.key) && dropDown.option('opened')) {
    if (e.key === 'Escape') e.preventDefault()
    e.stopPropagation()
    closeAndFocus()
  }
}

const moveRowSelection = async(grid, tabIndex, direction) => {
  const rows = grid.getVisibleRows().filter(row => row.rowType === 'data')
  if (!rows.length) return

  const focusedRowKey = grid.option('focusedRowKey')
  const focusedRowIndex = grid.option('focusedRowIndex')
  const currentIndex = rows.findIndex(row => row.key === focusedRowKey || row.rowIndex === focusedRowIndex)
  const nextIndex = currentIndex < 0 ? direction > 0 ? 0 : rows.length - 1 : Math.max(0, Math.min(rows.length - 1, currentIndex + direction))
  const row = rows[nextIndex]

  grid.option('focusedRowIndex', row.rowIndex)
  keyboardSelectionDepth++
  try {
    await grid.selectRows([row.key], false)
  } finally {
    keyboardSelectionDepth--
  }
  selectRow(row.data, tabIndex, false)
  focusActiveGrid()
}

const onGridKeyDown = (e, grid, tabIndex) => {
  if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Enter', 'Escape', 'Tab'].includes(e.key)) return
  e.stopPropagation()
  if (e.key !== 'Tab') e.preventDefault()

  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    moveRowSelection(grid, tabIndex, e.key === 'ArrowDown' ? 1 : -1)
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
    const direction = e.key === 'ArrowRight' ? 1 : -1
    activeTabIndex.value = (activeTabIndex.value + direction + props.tabs.length) % props.tabs.length
  } else if (e.key === 'Enter') {
    const row = grid.getVisibleRows().find(row => row.key === grid.option('focusedRowKey'))
    if (row) selectRow(row.data, tabIndex)
  } else {
    closeAndFocus()
  }
}

const focusActiveGrid = () => {
  if (hideDropDown.value || !dropDown.option('opened')) return
  const grid = dataGrids[activeTabIndex.value]
  if (!grid?.getVisibleRows().length) return
  grid.focus(grid.getCellElement(grid.option('focusedRowIndex'), 0))
}

const onDropDownInitialized = (e) => {
  dropDown = e.component
  e.element.addEventListener('keydown', onDropDownKeyDown, true)
}

const onDataGridInitialized = (e, tabIndex) => {
  dataGrids[tabIndex] = e.component
  e.element.addEventListener('keydown', (event) => onGridKeyDown(event, e.component, tabIndex), true)
}

const onRowSelected = (e, tabIndex) => {
  if (keyboardSelectionDepth > 0 || !e.selectedRowsData?.length) return
  selectRow(e.selectedRowsData[0], tabIndex)
}

const selectRow = (row, tabIndex, closeDropDown = true) => {
  const tab = props.tabs[tabIndex]
  const display = tab.getDisplay?.(row) ?? row._display ?? String(row.id)
  dropDown.option({ dataSource: [{ id: row.id, _display: display }], value: row.id })
  emit('valueChanged', { id: row.id, fieldName: tab.fieldName, _display: display })
  if (closeDropDown) closeAndFocus()
}
</script>

<template>
  <dx-drop-down-box
    v-model:opened="dropDownOpened"
    :value="props.value?.id ?? null"
    :data-source="dropDownDataSource"
    :disabled="props.disabled"
    display-expr="_display"
    value-expr="id"
    content-template="contentTemplate"
    :drop-down-options="dropDownOptions"
    :input-attr="{ readOnly: true, style: 'cursor: pointer' }"
    show-borders
    :is-valid="props.isValid"
    :validation-error="props.validationError"
    @initialized="onDropDownInitialized"
    @closed="onClosed"
  >
    <template #contentTemplate>
      <dx-tab-panel
        v-model:selectedIndex="activeTabIndex"
        :items="props.tabs"
        item-template="tabTemplate"
        :defer-rendering="false"
        :animation-enabled="false"
        height="100%"
        style="display: flex; flex-direction: column"
        @selection-changed="focusActiveGrid"
      >
        <template #tabTemplate="{ data: tab, index }">
          <div style="height: 100%; display: flex; flex-direction: column">
            <dx-data-grid
              :key="`grid-${tab.id}`"
              style="flex: 1; min-height: 0"
              :sync-lookup-filter-values="false"
              :data-source="tab.dataSource"
              :columns="tab.columns"
              :hover-state-enabled="true"
              :focused-row-enabled="true"
              :focused-row-index="0"
              :auto-navigate-to-focused-row="true"
              :remote-operations="true"
              :key-expr="null"
              @initialized="(e) => onDataGridInitialized(e, index)"
              @content-ready="onContentReady"
              @selection-changed="(e) => onRowSelected(e, index)"
            >
              <template #documentTypeCellTemplate="{ data: templateOptions }">
                <q-chip
                  size="sm"
                  class="q-ma-none"
                  text-color="white"
                  style="margin: 0"
                  :label="localizedDocumentTypes.find(x => x.id === templateOptions.data.sale_document_type)?.name"
                  :style="{ backgroundColor: localizedDocumentTypes.find(x => x.id === templateOptions.data.sale_document_type)?.color }"
                />
              </template>
              <template #typeCellTemplate="{ data: templateOptions }">
                <q-chip
                  v-if="!!templateOptions.data.type"
                  size="sm"
                  text-color="white"
                  style="margin: 0"
                  :label="registryStore.getLocalizedSubjectEntityTypes().find(x => x.id === templateOptions.data.type)?.name"
                  :color="registryStore.getLocalizedSubjectEntityTypes().find(x => x.id === templateOptions.data.type)?.color"
                />
              </template>
              <dx-filter-row :visible="true" />
              <dx-scrolling mode="virtual" />
              <dx-selection mode="single" />
            </dx-data-grid>
          </div>
        </template>
      </dx-tab-panel>
    </template>
  </dx-drop-down-box>
</template>
